import React, {useEffect, useRef, useState} from 'react';
import Translate, {t} from "../../../../../../../../../components/Translate";
import Input from "../../../../../../../../../components-new/form/Input";
import BlueButton from "../../../../../../../../../components-new/buttons/BlueButton";
import SilverButton from "../../../../../../../../../components-new/buttons/SilverButton";
import config from "../../../../../../../../../config";
import {
    getKnowledgeStatus,
    reindexKnowledge,
    searchKnowledge,
    updateKnowledgeMode
} from "../../../../../../../../../services/company/api";
import AiBlock from "./AiBlock";
import AiBlockPlaceholder from "./AiBlockPlaceholder";

const MODES = ['ON', 'OFF'];

const errorKeyOf = (error) =>
    error && error.response && typeof error.response.data === 'string'
        ? error.response.data
        : "somethingWentWrong";

/**
 * Controls how the uploaded sources are used and lets the company try the
 * assistant's own search. Without the trial search a company has no way to tell
 * a good source from a useless one until a customer complains.
 */
const AiKnowledgeIndex = ({client, aiType, user, aiSupportedLanguages, knowledgeVersion, addNotification}) => {

    // Team AI keeps its own flow, so the knowledge search does not apply there
    const isSupported = aiType === config.AITypes.GPT_AI || aiType === config.AITypes.GEMINI_AI;

    const [status, setStatus] = useState(null);
    const [failed, setFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [query, setQuery] = useState('');
    const [hits, setHits] = useState(null);
    const [isSearching, setIsSearching] = useState(false);

    // the reload below fires seconds after the click, by which time the user may
    // have left the tab; writing state then warns and leaks the timer
    const mountedRef = useRef(true);
    const reloadTimerRef = useRef(null);

    useEffect(() => () => {
        mountedRef.current = false;
        if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
    }, []);

    const loadStatus = () => getKnowledgeStatus(client.id)
        .then(data => {
            if (mountedRef.current) setStatus(data);
        })
        .catch(() => {
        });

    useEffect(() => {
        let active = true;

        if (!isSupported) return;

        setFailed(false);

        getKnowledgeStatus(client.id)
            .then(data => {
                if (active) setStatus(data);
            })
            .catch(() => {
                if (active) setFailed(true);
            });

        return () => {
            active = false;
        };
        // knowledgeVersion changes when a source is added, removed or finishes
        // crawling, and every one of those moves the counters below
    }, [client.id, aiType, knowledgeVersion]);

    const changeMode = (mode) => {
        if (!status || status.mode === mode || isSaving) return;
        setIsSaving(true);

        updateKnowledgeMode(client.id, mode)
            .then(() => {
                setStatus({...status, mode});
                addNotification("successful", config.notificationLevels.success);
            })
            .catch(error => addNotification(errorKeyOf(error), config.notificationLevels.error))
            .finally(() => setIsSaving(false));
    };

    const reindex = () => {
        setIsSaving(true);

        reindexKnowledge(client.id)
            .then(() => {
                addNotification("aiKnowledgeReindexStarted", config.notificationLevels.success);
                // the work runs in the background, so the counters move a little later
                reloadTimerRef.current = setTimeout(loadStatus, 4000);
            })
            .catch(error => addNotification(errorKeyOf(error), config.notificationLevels.error))
            .finally(() => setIsSaving(false));
    };

    const runSearch = () => {
        const value = query.trim();
        if (!value) return;

        setIsSearching(true);
        setHits(null);

        searchKnowledge(client.id, value)
            .then(setHits)
            .catch(error => addNotification(errorKeyOf(error), config.notificationLevels.error))
            .finally(() => setIsSearching(false));
    };

    // both guards sit after the hooks on purpose: returning earlier would change
    // the number of hooks between renders, and React fails the whole screen when
    // the company switches its AI type in the selector above
    if (!isSupported) return null;

    // see the note in AiStyle: a failed load is not an absent setting
    if (!status) {
        return (
            <AiBlock titleKey="aiKnowledgeIndex">
                <AiBlockPlaceholder failed={failed} onRetry={() => setAttempt(a => a + 1)}/>
            </AiBlock>
        );
    }

    const pending = status.pendingChunks || 0;

    // Questions in a language the sources are not written in have to be translated
    // before the search can match anything, which costs about a second. Naming the
    // gap lets the company close it instead of wondering why some answers lag.
    const knowledgeLanguages = (status && status.languages) || [];

    const supportedLanguages = aiSupportedLanguages === 'all'
        ? config.aiSupportedLanguages.filter(l => l.code !== 'all').map(l => l.code)
        : (aiSupportedLanguages || '').split(',').map(l => l.trim().toLowerCase()).filter(Boolean);

    const missingLanguages = knowledgeLanguages.length
        ? supportedLanguages.filter(l => knowledgeLanguages.indexOf(l) === -1)
        : [];


    return (
        <AiBlock titleKey="aiKnowledgeIndex" hintKey="aiKnowledgeIndexHint">
            <div className="ai-index">
                <div className="ai-index__modes">
                    {MODES.map(mode => (
                        <button
                            key={mode}
                            type="button"
                            className={'ai-index__mode' + (status.mode === mode ? ' ai-index__mode--selected' : '')}
                            disabled={isSaving}
                            onClick={() => changeMode(mode)}
                        >
                            <span className="ai-index__mode-title">
                                <Translate text={'aiKnowledgeMode' + mode}/>
                            </span>
                            <span className="ai-index__mode-text">
                                <Translate text={'aiKnowledgeMode' + mode + 'Expl'}/>
                            </span>
                        </button>
                    ))}
                </div>

                <div className="ai-index__stats">
                    <div className="ai-index__stat">
                        <span className="ai-index__stat-value">{status.totalChunks}</span>
                        <span className="ai-index__stat-label"><Translate text="aiKnowledgeChunks"/></span>
                    </div>
                    <div className="ai-index__stat">
                        <span className="ai-index__stat-value">{status.embeddedChunks}</span>
                        <span className="ai-index__stat-label"><Translate text="aiKnowledgeEmbedded"/></span>
                    </div>
                    {knowledgeLanguages.length > 0 && (
                        <div className="ai-index__stat">
                            <span className="ai-index__stat-value ai-index__stat-value--text">
                                {knowledgeLanguages.map(l => l.toUpperCase()).join(', ')}
                            </span>
                            <span className="ai-index__stat-label"><Translate text="aiKnowledgeLanguages"/></span>
                        </div>
                    )}

                    {status.embeddingModel && (
                        <div className="ai-index__stat">
                            <span className="ai-index__stat-value ai-index__stat-value--text">
                                {status.embeddingModel}
                            </span>
                            <span className="ai-index__stat-label"><Translate text="aiKnowledgeModel"/></span>
                        </div>
                    )}
                </div>

                {missingLanguages.length > 0 && (
                    <div className="ai-index__advice">
                        <Translate text="aiKnowledgeLanguageGap"/>
                        {' '}
                        <b>{missingLanguages.map(l => l.toUpperCase()).join(', ')}</b>.
                        {' '}
                        <Translate text="aiKnowledgeLanguageGapTip"/>
                    </div>
                )}

                {pending > 0 && (
                    <div className="ai-index__pending">
                        <Translate text="aiKnowledgePending"/>: {pending}
                    </div>
                )}

                {/* nothing to finish means nothing to offer: a disabled button with
                    no explanation next to it only raises questions */}
                {pending > 0 && (
                    <div className="ai-index__row">
                        <SilverButton
                            width={'auto'}
                            height={'40px'}
                            styles={{padding: '0 20px', whiteSpace: 'nowrap'}}
                            disabled={isSaving}
                            onClick={reindex}
                        >
                            <Translate text="aiKnowledgeReindex"/>
                        </SilverButton>
                    </div>
                )}

                <div className="ai-index__search">
                    <p className="ai-index__search-text"><Translate text="aiKnowledgeTestExpl"/></p>

                    <div className="ai-index__row">
                        <Input
                            style={{flex: '1 1 260px'}}
                            placeholder={t({text: 'aiKnowledgeTestPlaceholder', user})}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                            disabled={isSearching}
                        />

                        <BlueButton
                            width={'auto'}
                            height={'40px'}
                            styles={{minWidth: '150px', padding: '0 20px', whiteSpace: 'nowrap'}}
                            disabled={!query.trim() || isSearching}
                            onClick={runSearch}
                        >
                            <Translate text="aiKnowledgeTestRun"/>
                        </BlueButton>
                    </div>

                    {hits && hits.length === 0 && (
                        <div className="ai-index__empty"><Translate text="aiKnowledgeTestNothing"/></div>
                    )}

                    {hits && hits.length > 0 && (
                        <div className="ai-index__hits">
                            {hits.map(hit => (
                                <div
                                    key={hit.chunkId}
                                    className={'ai-index__hit' + (hit.usedForAnswer ? ' ai-index__hit--used' : '')}
                                >
                                    <div className="ai-index__hit-head">
                                        <span className="ai-index__hit-title">{hit.title || hit.reference}</span>
                                        <span className="ai-index__hit-badge">
                                            <Translate text={hit.usedForAnswer ? 'aiKnowledgeHitUsed' : 'aiKnowledgeHitSkipped'}/>
                                        </span>
                                    </div>
                                    <div className="ai-index__hit-text">{hit.content}</div>
                                    <div className="ai-index__hit-scores">
                                        <Translate text="aiKnowledgeHitMeaning"/>: {hit.vectorScore.toFixed(2)}
                                        {' · '}
                                        <Translate text="aiKnowledgeHitWords"/>: {hit.keywordScore.toFixed(2)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </AiBlock>
    );
};

export default AiKnowledgeIndex;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/management/clients/new/configurations/components/AI/AiKnowledgeIndex.jsx